import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Package, BookBookmark, FilePdf, Minus, Plus, ArrowLeft } from "@phosphor-icons/react";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { TrustBadges } from "@/components/store/TrustBadges";
import { Stars } from "@/components/store/ReadersWall";

const PRICE = { physical: "physical_price", hardcover: "hardcover_price", ebook: "ebook_price" };

function FormatOption({ active, disabled, onClick, icon: Icon, title, price, note, testId }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick} data-testid={testId}
      className={`flex w-full items-start gap-4 rounded-2xl border-2 p-4 text-left transition-[border-color,background-color,transform] duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${active ? "border-primary bg-primary/5" : "border-border bg-white hover:border-foreground/30"}`}>
      <Icon weight="duotone" size={28} className={active ? "text-primary" : "text-muted-foreground"} />
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <p className="font-semibold">{title}</p>
          <p className="font-display text-lg">{inr(price)}</p>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{note}</p>
      </div>
    </button>
  );
}

export default function BookDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { data: book, isLoading, error } = useQuery({ queryKey: ["book", slug], queryFn: async () => (await api.get(`/books/${slug}`)).data });
  const [format, setFormat] = useState(null);
  const [qty, setQty] = useState(1);

  if (isLoading) return <div className="mx-auto max-w-7xl px-5 py-24 text-sm text-muted-foreground">Loading…</div>;
  if (error || !book) return <div className="mx-auto max-w-7xl px-5 py-24" data-testid="book-not-found">Book not found. <Link to="/" className="text-primary underline">Back to the shelf</Link></div>;

  const physicalOk = book.physical_enabled && book.stock > 0;
  const hardcoverOk = book.hardcover_enabled && book.hardcover_stock > 0;
  const ebookOk = book.ebook_enabled && book.has_pdf;
  const chosen = format || (physicalOk ? "physical" : hardcoverOk ? "hardcover" : ebookOk ? "ebook" : null);
  const maxQty = chosen === "hardcover" ? book.hardcover_stock : chosen === "physical" ? book.stock : 1;

  const handleAdd = (goToCart) => {
    if (!chosen) return toast.error("This title is currently unavailable");
    add(book, chosen, qty);
    toast.success(`${book.title} added to your bag`);
    if (goToCart) navigate("/cart");
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8 md:py-20" data-testid="book-detail-page">
      <Link to="/#shelf" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-shelf-link"><ArrowLeft /> Back to the shelf</Link>
      <div className="mt-8 grid gap-12 md:grid-cols-12 md:gap-16">
        <div className="md:col-span-5">
          <div className="sticky top-24 mx-auto w-[260px] sm:w-[340px] md:w-full md:max-w-md">
            <div className="absolute -inset-8 -z-10 rounded-[2.5rem] bg-secondary/60 blur-3xl" />
            <img src={book.cover_url} alt={book.title} className="cover-shadow aspect-[2/3] w-full rounded-md object-cover" data-testid="book-detail-cover" />
          </div>
        </div>
        <div className="md:col-span-7 lg:col-span-6">
          <p className="eyebrow">{book.genre || "Book"} · {book.published_year}</p>
          <h1 className="mt-3 text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl" data-testid="book-detail-title">{book.title}</h1>
          {book.subtitle && <p className="mt-3 font-display text-xl italic text-muted-foreground">{book.subtitle}</p>}
          {book.rating_avg && (
            <a href="/#readers" className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary" data-testid="book-rating-summary">
              <Stars value={Math.round(book.rating_avg)} size={16} />
              <span className="font-semibold text-foreground">{book.rating_avg}</span> · {book.rating_count} {book.rating_count === 1 ? "reader" : "readers"}
            </a>
          )}
          <p className="mt-8 whitespace-pre-line text-[15px] leading-relaxed text-foreground/85" data-testid="book-detail-description">{book.description}</p>

          <div className="mt-10 space-y-3">
            <p className="text-sm font-semibold">Choose a format</p>
            {book.physical_enabled && (
              <FormatOption active={chosen === "physical"} disabled={!physicalOk} onClick={() => { setFormat("physical"); setQty(1); }} icon={Package} title="Paperback" price={book.physical_price} testId="format-option-physical"
                note={physicalOk ? `${book.stock} ${book.stock === 1 ? "copy" : "copies"} available · ships in 2–3 working days` : "Sold out — check back soon"} />
            )}
            {book.hardcover_enabled && (
              <FormatOption active={chosen === "hardcover"} disabled={!hardcoverOk} onClick={() => { setFormat("hardcover"); setQty(1); }} icon={BookBookmark} title="Hardcover" price={book.hardcover_price} testId="format-option-hardcover"
                note={hardcoverOk ? `${book.hardcover_stock} ${book.hardcover_stock === 1 ? "copy" : "copies"} available · cloth-bound keepsake edition` : "Sold out — check back soon"} />
            )}
            {book.ebook_enabled && (
              <FormatOption active={chosen === "ebook"} disabled={!ebookOk} onClick={() => setFormat("ebook")} icon={FilePdf} title="Ebook (PDF)" price={book.ebook_price} testId="format-option-ebook"
                note={ebookOk ? "Instant download · personalised with your name · re-download anytime" : "Ebook coming soon"} />
            )}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            {chosen && chosen !== "ebook" && (
              <div className="flex items-center rounded-sm border border-border bg-white" data-testid="qty-selector">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:text-primary" data-testid="qty-decrease-button"><Minus /></button>
                <span className="w-6 text-center text-sm font-semibold" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(Math.min(maxQty, qty + 1))} className="p-3 hover:text-primary" data-testid="qty-increase-button"><Plus /></button>
              </div>
            )}
            <button onClick={() => handleAdd(true)} disabled={!chosen} className="rounded-sm bg-primary px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.1em] text-white transition-colors duration-200 hover:bg-[#702F2A] disabled:opacity-40" data-testid="buy-now-button">
              Buy now · {inr((book[PRICE[chosen]] || 0) * (chosen === "ebook" ? 1 : qty))}
            </button>
            <button onClick={() => handleAdd(false)} disabled={!chosen} className="rounded-sm border border-foreground/20 px-6 py-3.5 text-sm font-semibold transition-colors hover:bg-muted disabled:opacity-40" data-testid="add-to-cart-button">Add to bag</button>
          </div>

          <dl className="mt-12 grid grid-cols-3 gap-4 border-t border-border pt-6 text-sm">
            {book.pages && <div><dt className="text-muted-foreground">Pages</dt><dd className="font-semibold">{book.pages}</dd></div>}
            {book.isbn && <div><dt className="text-muted-foreground">ISBN</dt><dd className="font-semibold">{book.isbn}</dd></div>}
            {book.published_year && <div><dt className="text-muted-foreground">Published</dt><dd className="font-semibold">{book.published_year}</dd></div>}
          </dl>
          <div className="mt-10"><TrustBadges compact /></div>
        </div>
      </div>
    </div>
  );
}
